import { jsx, jsxs } from 'react/jsx-runtime';
import { isNil } from '@flex-design-system/fx-utils/common';
import { useTimeout, useResizeObserver } from '@flex-design-system/fx-utils/hooks';
import { styled } from '@flex-design-system/stitches-react';
import { composeEventHandlers } from '@radix-ui/primitive';
import { useComposedRefs } from '@radix-ui/react-compose-refs';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { useControllableState } from '@radix-ui/react-use-controllable-state';
import classNames from 'classnames';
import { forwardRef, useRef, useState, useCallback, useEffect, useLayoutEffect } from 'react';
import { logger } from '../../utils/logger.mjs';
import { SegmentedControlProvider, useSegmentedControlContext } from './context.mjs';
import { segmentedControl, segmentedControlItem, segmentedControlItemClickable, segmentedControlIndicator } from './segmentedControl.vanilla.mjs';

const Root = forwardRef(
  (props, ref) => {
    const {
      disabled,
      children,
      onValueChange,
      size = "default",
      defaultValue,
      value: valueFromProps,
      fullWidth = false,
      ...rootProps
    } = props;
    const defaultValueRef = useRef(defaultValue);
    if (defaultValueRef.current !== defaultValue) {
      logger.warn(
        `defaultValue\uAC00 ${defaultValueRef.current} \uC5D0\uC11C ${defaultValue}\uB85C \uBCC0\uACBD\uB418\uC5C8\uC2B5\uB2C8\uB2E4. \uBCC0\uACBD\uB418\uB294 \uAC12\uC740 value\uB97C \uC9C1\uC811 \uBCC0\uACBD\uD558\uB294 \uACBD\uC6B0 value prop\uC744 \uC0AC\uC6A9\uD574 \uC8FC\uC138\uC694.`
      );
    }
    const [value = "", setValue] = useControllableState({
      prop: valueFromProps,
      defaultProp: defaultValue,
      onChange: onValueChange
    });
    const [selectedElement, setSelectedElement] = useState(
      null
    );
    const rootRef = useRef(null);
    const [rootElement, setRootElement] = useState(null);
    const composedRef = useComposedRefs(ref, rootRef, (el) => setRootElement(el));
    const setActiveTabByValue = useCallback((dataKey) => {
      if (rootRef.current) {
        const _selectedElement = rootRef.current.querySelector(`[${itemDataKey}="${dataKey}"]`);
        if (_selectedElement !== null) {
          setSelectedElement(_selectedElement);
        }
      }
    }, []);
    useEffect(() => {
      if (defaultValueRef.current !== void 0) {
        setActiveTabByValue(defaultValueRef.current);
      }
    }, [setActiveTabByValue]);
    useEffect(() => {
      if (!isNil(value)) {
        setActiveTabByValue(value);
      }
    }, [setActiveTabByValue, value]);
    return /* @__PURE__ */ jsx(SegmentedControlProvider, { disabled, value, size, children: /* @__PURE__ */ jsxs(
      StyledRadioGroupRoot,
      {
        ...rootProps,
        className: classNames(
          segmentedControl({ fullWidth, size }),
          rootProps.className
        ),
        defaultValue,
        value,
        ref: composedRef,
        onValueChange: composeEventHandlers(setValue, setActiveTabByValue),
        children: [
          /* @__PURE__ */ jsx(
            ActiveBox,
            {
              selectedElement,
              size,
              rootElement
            }
          ),
          children
        ]
      }
    ) });
  }
);
const itemDataKey = "data-key";
const Item = forwardRef((props, ref) => {
  const { children, disabled: disabledFromProps, value, ...itemProps } = props;
  const {
    size,
    disabled: disabledFromContext,
    value: valueFromContext
  } = useSegmentedControlContext("SegmentedControl.Item");
  const disabled = disabledFromProps || disabledFromContext;
  return /* @__PURE__ */ jsx(
    StyledRadioGroupItem,
    {
      ...itemProps,
      className: classNames(
        segmentedControlItem({
          controlActive: valueFromContext === value,
          size
        }),
        !disabled && segmentedControlItemClickable,
        itemProps.className
      ),
      ref,
      value,
      disabled,
      ...{ [itemDataKey]: value },
      children
    }
  );
});
function ActiveBox(props) {
  const initialRender = useRef(true);
  const { setTimeout } = useTimeout();
  const { selectedElement, size, rootElement } = props;
  const [style, setStyle] = useState({
    transform: "",
    width: "",
    height: ""
  });
  const updateStyle = useCallback(() => {
    if (selectedElement !== null) {
      const offsetLeft = selectedElement.offsetLeft;
      const width = selectedElement.offsetWidth;
      const rootElementWidth = rootElement?.offsetWidth ?? 0;
      const max = rootElementWidth - width - 1;
      const min = 1;
      const offset = Math.min(max, Math.max(min, offsetLeft));
      const transform = `translateX(${offset}px)`;
      setStyle((prev) => ({
        ...prev,
        transform,
        width: `${width}px`
      }));
    }
  }, [rootElement?.offsetWidth, selectedElement]);
  useEffect(() => {
    updateStyle();
  }, [updateStyle]);
  useLayoutEffect(() => {
    setTimeout(() => initialRender.current = false, 100);
  }, [setTimeout]);
  useResizeObserver(rootElement, updateStyle);
  return /* @__PURE__ */ jsx(
    "div",
    {
      style,
      className: segmentedControlIndicator({
        animation: !initialRender.current,
        size
      })
    }
  );
}
const StyledRadioGroupRoot = styled(RadioGroupPrimitive.Root);
const StyledRadioGroupItem = styled(RadioGroupPrimitive.Item);
const SegmentedControl = Object.assign({}, Root, {
  Item
});

export { SegmentedControl as default };
//# sourceMappingURL=SegmentedControl.mjs.map
